








































































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Playlist } from '@/types';
import { PlaylistService, OrderDir, PlaylistListParams, PlaylistOrderColumn } from '@/api';
import { TableField } from '@/ui-lib/table/Table.vue';
import UPagination from '@/ui-lib/pagination/Pagination.vue';
import UTable from '@/ui-lib/table/Table.vue';
import { convertTimeHHMMSS } from '@/utils/convertTimeHHMMSS';
import { preset } from '@/store/modules/preset';
import UCheckbox from '@/ui-lib/checkbox/Checkbox.vue';
import UButton from '@/ui-lib/button/Button.vue';
import UTextField from '@/ui-lib/text-field/TextField.vue';

export type PlaylistsSelectModalParams = {
  selected?: number[];
  submitLabel?: string;
  onSelect?: () => void;
};

@Component({
  components: {
    UButton,
    UCheckbox,
    UPagination,
    UTable,
    UTextField
  }
})
export default class extends Vue {
  private selected: number[] = [];
  private submitLabel = 'Продолжить';
  private onSelect: () => void = () => {
    return;
  };

  private start = 0;
  private limit = 15;
  private search = '';
  private appliedSearch = '';
  private searchTimeout = 0;
  private loadRequestID = 0;
  private isLoading = false;
  private list: Playlist[] = [];
  private total = 0;
  private titles: Record<number, string> = {};

  // Дебаунс живёт в методе, а не в поле-стрелке: инициализаторы полей
  // vue-class-component выполняются на объекте-коллекторе data, и захваченный
  // ими this не проксирует реактивные свойства — присвоения в него теряются.
  @Watch('search')
  private searchChanged() {
    clearTimeout(this.searchTimeout);

    this.searchTimeout = window.setTimeout(() => {
      if (this.search === this.appliedSearch) {
        return;
      }

      this.start = 0;
      this.load();
    }, 300);
  }

  private beforeClose() {
    clearTimeout(this.searchTimeout);

    // Модалка живёт в DOM постоянно: без инвалидации ответ уже отправленного
    // запроса всплыл бы тостом об ошибке поверх закрытого окна.
    this.loadRequestID++;
  }

  private close(): void {
    this.$modal.hide('playlists-select');
  }

  private beforeOpen({ params }: { params: PlaylistsSelectModalParams }) {
    this.start = 0;
    this.isLoading = false;
    this.search = '';
    this.list = [];
    this.total = 0;
    this.titles = {};
    this.selected = params?.selected || [];
    this.submitLabel = params?.submitLabel || 'Продолжить';
    this.onSelect =
      params?.onSelect ||
      (() => {
        return;
      });

    this.load();
  }

  private async load() {
    this.isLoading = true;

    // Ответы приходят вразнобой при быстрой печати и переоткрытии модалки —
    // применяем только последний отправленный запрос.
    const requestID = ++this.loadRequestID;

    try {
      const params: PlaylistListParams = {
        start: this.start,
        limit: this.limit,
        orderColumn: PlaylistOrderColumn.id,
        orderDir: OrderDir.desc
      };

      if (this.search) {
        params.search = this.search;
      }

      this.appliedSearch = this.search;

      // Picks made elsewhere (a filter from the address) come as bare ids; their titles arrive
      // with the page so the chips don't flash ids first.
      const [{ list, total }, chosen] = await Promise.all([
        PlaylistService.list(params),
        this.loadMissingTitles()
      ]);

      if (requestID !== this.loadRequestID) {
        return;
      }

      const titles = { ...this.titles };
      [...chosen, ...list].forEach((pl) => (titles[pl.id] = pl.title));
      this.titles = titles;

      this.list = list;
      this.total = total;
    } catch (e) {
      if (requestID !== this.loadRequestID) {
        return;
      }

      this.$notify({
        type: 'error',
        title: 'Ошибка',
        text: e?.message || 'Неизвестная ошибка'
      });
    }

    this.isLoading = false;
  }

  private async loadMissingTitles(): Promise<Playlist[]> {
    try {
      return await PlaylistService.listByIDs(this.selected.filter((id) => !(id in this.titles)));
    } catch (e) {
      // A chip without a title falls back to the id and still removes the pick.
      return [];
    }
  }

  private titleOf(id: number): string {
    return this.titles[id] || `#${id}`;
  }

  private unselect(id: number) {
    const i = this.selected.indexOf(id);

    if (i >= 0) {
      this.selected.splice(i, 1);
    }
  }

  private get tableFields(): TableField[] {
    return [
      {
        name: 'select',
        label: '',
        width: '52px'
      },

      {
        name: 'id',
        label: 'ID',
        width: '65px'
      },

      {
        name: 'image',
        label: '',
        width: '60px'
      },

      {
        name: 'title',
        label: 'Название'
      },

      {
        name: 'musicPresetIDs',
        label: 'Пресеты',
        width: '170px'
      },

      {
        name: 'totalTracks',
        label: 'Треки',
        align: 'right',
        width: '100px'
      },

      {
        name: 'totalDuration',
        label: 'Длительность',
        align: 'right',
        width: '150px'
      }
    ];
  }

  private get presets() {
    return preset.context(this.$store).state.list;
  }

  private presetsName(pl: Playlist) {
    return (
      this.presets
        .filter((g) => pl.musicPresetIDs.includes(g.id))
        .map((g) => g.name)
        .join(', ') || 'Неизвестно'
    );
  }

  private durationText(pl: Playlist) {
    return convertTimeHHMMSS(pl.totalDuration);
  }

  private changeStart(val: number) {
    clearTimeout(this.searchTimeout);

    // Неприменённый ввод в поиске делает выбранную страницу бессмысленной:
    // клик по пагинации применяет запрос с первой страницы.
    this.start = this.search === this.appliedSearch ? val : 0;
    this.load();
  }

  private select() {
    this.onSelect();
    this.close();
  }

  private toggleSelect(row: Playlist, val: boolean) {
    const i = this.selected.indexOf(row.id);

    if (val) {
      if (i === -1) {
        this.selected.push(row.id);
      }
    } else {
      if (i >= 0) {
        this.selected.splice(i, 1);
      }
    }
  }

  private selectAll() {
    this.list.forEach((item) => {
      const i = this.selected.indexOf(item.id);

      if (i === -1) {
        this.selected.push(item.id);
      }
    });
  }

  private unselectAll() {
    this.selected.splice(0);
  }
}
